import dayjs from 'dayjs';
import round from 'lodash/round';

import { AggressiveModeConfig, NormalizedRebalancingPlan } from '@cast/types';
import { KentAggressiveModeConfig } from '@cast/types-kent';

import { aggregateOperations } from 'utils/cluster';

import {
  RebalancePlanSimulationProgress,
  RebalanceProgressPercentage,
} from '../_types';

/**
 * Checks if at least one aggressive mode config is enabled.
 * Only boolean properties are checked because some configs (e.g. Kent) include
 * non-boolean fields like `drainTimeoutSeconds` that don't indicate an
 * enabled aggressive mode setting on their own.
 */
export const isAtLeastOneAggressiveModeConfigEnabled = (
  aggressiveModeConfig: AggressiveModeConfig | KentAggressiveModeConfig
): boolean => {
  return Object.values(aggressiveModeConfig).some(
    (value) => typeof value === 'boolean' && value
  );
};

type CalculateSimulatedProgressArgs = {
  /** Time in seconds use to create progress simulation */
  timePassed: number;

  /** Single type operations count used to get simulation percentage in proportion to single type operations. */
  totalSimulatedOperations: number;

  /** All operations count used to get simulation percentage in proportion to all operations. */
  totalAllOperations: number;

  /** Limits simulation duration. */
  maxSimulationDuration?: number;
};

/**
 * Calculating simulated progress so the user could see that something is happening while first operation is still being executed.
 * Progress is calculated separately for single type and all operations counts.
 * I.e.: total ops - 10, create ops - 2, if we simulate that first operation is 20% done result is for all ops 2% and for create ops - 10%.
 *
 * @return Progress percentages in proportion to single type and all operations counts.
 */
export const calculateSimulatedProgress = ({
  timePassed,
  totalSimulatedOperations,
  totalAllOperations,
  maxSimulationDuration = 60,
}: CalculateSimulatedProgressArgs): RebalancePlanSimulationProgress => {
  const maxProgressForSimulatedOperations = round(
    (1 / totalSimulatedOperations) * 100,
    1
  );
  const maxProgressForAllOperations = round((1 / totalAllOperations) * 100, 1);

  if (timePassed >= maxSimulationDuration) {
    return {
      simulatedProgress: maxProgressForSimulatedOperations,
      overallSimulatedProgress: maxProgressForAllOperations,
    };
  }
  return {
    simulatedProgress: round(
      (timePassed / maxSimulationDuration) * maxProgressForSimulatedOperations,
      1
    ),
    overallSimulatedProgress: round(
      (timePassed / maxSimulationDuration) * maxProgressForAllOperations,
      1
    ),
  };
};

const calculateOperationProgress = (
  completed: number,
  total: number
): number => {
  if (!total) {
    return 0;
  }
  return round((completed * 100) / total, 1);
};

export const getRebalanceProgress = (
  rebalancePlan?: NormalizedRebalancingPlan
): RebalanceProgressPercentage => {
  if (!rebalancePlan) {
    return {
      create: null,
      cleanup: null,
      overall: null,
    };
  }

  const operationCount = aggregateOperations(rebalancePlan?.operations);
  const cleanUpOpsCount = operationCount.drain + operationCount.delete;
  const cleanedUpOpsCount = operationCount.drained + operationCount.deleted;

  const hasCreateOps = Boolean(operationCount.create);

  const rebalanceLengthInSeconds = rebalancePlan.triggeredAt
    ? dayjs().diff(dayjs(rebalancePlan.triggeredAt), 'second')
    : 0;

  const { simulatedProgress, overallSimulatedProgress } =
    calculateSimulatedProgress({
      timePassed: rebalanceLengthInSeconds,
      totalSimulatedOperations: hasCreateOps
        ? operationCount.create
        : cleanUpOpsCount,
      totalAllOperations: operationCount.total,
    });

  const createdOperationsPercentage = Math.max(
    simulatedProgress,
    calculateOperationProgress(operationCount.created, operationCount.create)
  );

  const cleanedUpOperationsPercentage = Math.max(
    !hasCreateOps ? simulatedProgress : 0,
    calculateOperationProgress(cleanedUpOpsCount, cleanUpOpsCount)
  );

  const overallCompletedOperations = operationCount.created + cleanedUpOpsCount;
  const overallPercentage = Math.max(
    calculateOperationProgress(
      overallCompletedOperations,
      operationCount.total
    ),
    overallSimulatedProgress
  );

  return {
    create: {
      percentage: createdOperationsPercentage,
      operations: {
        completed: operationCount.created,
        total: operationCount.create,
      },
    },
    cleanup: {
      percentage: cleanedUpOperationsPercentage, // drain + delete operations percentage
      operations: {
        completed: operationCount.deleted, // cleanup operations counts once node is deleted
        total: operationCount.delete, // total cleanup operations count for delete node
      },
    },
    overall: {
      percentage: overallPercentage,
      operations: {
        completed: overallCompletedOperations,
        total: operationCount.total,
      },
    },
  };
};
